<template>
  <div
    :class="{ 'w-full': !hasImages }"
    class="product-photo-gallery justify-center"
  >
    <div
      v-if="hasImages"
      class="hidden product-photo-gallery-desktop-navigation"
    >
      <div
        v-for="({ thumbnailImage }, index) in productImages"
        :key="`${thumbnailImage}-${index}`"
        @click="setActive(index)"
      >
        <transition name="fade" appear>
          <div class="thumbnail-image-frame">
            <OImage
              :img-class="`thumbnail-image rounded ${index === activeSlide ? 'opacity-100' : 'opacity-75'}`"
              v-bind="thumbnailImage"
            />
          </div>
        </transition>
      </div>
    </div>
    <div
      v-if="hasImages"
    >
      <VueSlickCarousel ref="carousel" v-bind="sliderOptions" @afterChange="activeSlide = $event">
        <div
          v-for="({ mainImage }, index) in productImages"
          :key="`${mainImage}-${index}`"
        >
          <transition name="fade" appear>
            <div class="main-image-frame">
              <OImage img-class="main-image" v-bind="mainImage" :custom-breakpoint="480" />
            </div>
          </transition>
        </div>

        <template #customPaging="page">
          <transition name="fade" appear>
            <div class="thumbnail-image-frame">
              <OImage
                :img-class="`thumbnail-image rounded ${page === activeSlide ? 'opacity-100' : 'opacity-75'}`"
                v-bind="productImages[page].thumbnailImage"
              />
            </div>
          </transition>
        </template>
      </VueSlickCarousel>
    </div>
  </div>
</template>

<script>
import OImage from '@/components/OImage'
import { getImagePaths } from '@/utils/general'
import { transformProductName } from '@/utils/webshop'
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'

export default {
  name: 'ProductPhotoGallery',
  components: {
    VueSlickCarousel,
    OImage
  },
  props: {
    images: {
      type: Array,
      default: () => ([]),
      validator: array => array.every(obj => ['mainImage', 'thumbnailImage'].every(prop => obj.hasOwnProperty(prop)))
    },
    productSlug: {
      type: String,
      default: ''
    },
    amountOfPhotosToShow: {
      type: Number,
      default: 4
    }
  },
  data () {
    return {
      activeSlide: 0
    }
  },
  computed: {
    sliderOptions () {
      return {
        arrows: false,
        dots: true
      }
    },
    productImages () {
      if (this.productSlug) {
        return [...Array(this.amountOfPhotosToShow)].map((_, index) => (
          {
            mainImage: getImagePaths({ path: `/images/frontend/loyalty/products/${transformProductName(this.productSlug)}/detail/product-detail-${index + 1}-big`, s3: true }),
            thumbnailImage: getImagePaths({ path: `/images/frontend/loyalty/products/${transformProductName(this.productSlug)}/detail/product-detail-${index + 1}-small`, s3: true })
          }
        ))
      }

      return this.images
    },
    hasImages () {
      return this.productSlug || this.images.length
    }
  },
  methods: {
    setActive (index) {
      this.$refs.carousel.goTo(index)
    }
  }
}
</script>

<style lang="postcss" scoped>
.product-photo-gallery {
  min-height: 62.13rem;

  >>> .main-image-frame {
    max-height: 42rem;
  }

  >>> .main-image {
    @apply object-cover max-h-full max-w-full !important;
  }

  >>> .slick-list {
    margin-bottom: .5rem;
  }

  >>> .slick-dots {
    @apply flex overflow-x-scroll pb-15 !important;
    margin-bottom: -6rem;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */

    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari/Webkit */
    }

    .thumbnail-image-frame {
      @apply mr-rem cursor-pointer;
      height: 14.6rem;
      width: 14.6rem;
    }

    .thumbnail-image {
      @apply object-cover max-h-full max-w-full !important;
    }
  }
}

.fade-enter-active {
  @apply transition-opacity ease-in duration-500 delay-200;
}

.fade-enter {
  @apply opacity-0;
}

.thumbnail-image-frame {
  @apply mr-rem cursor-pointer;
  margin-bottom: .85rem;
  height: 14.6rem;
  width: 14.6rem;
}

.oimage >>> .thumbnail-image {
  @apply object-cover max-h-full max-w-full !important;
}

@media (min-width: 480px) {
  .product-photo-gallery {
    @apply flex;

    &-desktop-navigation {
      width: 15.7rem;
      @apply block;
    }

    .slick-slider {
      @apply rounded overflow-hidden inline-block;
      max-width: 38.8rem;
      max-height: 61rem;

      >>> .slick-list {
        @apply mb-0;
      }
    }

    >>> .main-image-frame {
      max-height: 61rem;
    }

    >>> .slick-dots {
      @apply flex-col;
      min-width: 15.7rem;

      .thumbnail-image-frame {
        @apply hidden;
      }
    }
  }
}
</style>
